import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { to: '/', label: '首页', icon: 'ri-home-5-line', end: true },
  { to: '/category/movie', label: '电影', icon: 'ri-film-line' },
  { to: '/category/drama', label: '电视剧', icon: 'ri-tv-2-line' },
  { to: '/category/variety', label: '综艺', icon: 'ri-mic-line' },
  { to: '/category/short', label: '短视频', icon: 'ri-smartphone-line' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50'
      }`}
    >
      <div ref={wrapperRef} className="w-full px-4 md:px-6 h-16 flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-play-fill text-background-50 text-xl"></i>
          </span>
          <span className="font-display font-bold text-lg text-foreground-950 whitespace-nowrap">
            在线视频
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-primary-700 bg-primary-100'
                    : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、演员、类型"
              className="w-56 lg:w-72 h-10 pl-9 pr-3 rounded-full border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
        </form>

        <button
          type="button"
          aria-label="切换菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
        >
          <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频、演员、类型"
                className="w-full h-11 pl-9 pr-3 rounded-full border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium ${
                    isActive
                      ? 'text-primary-700 bg-primary-100'
                      : 'text-foreground-700 bg-background-100'
                  }`
                }
              >
                <i className={`${item.icon} text-base`}></i>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}